import { useCallback, useRef, useState, type ElementType, type ReactNode, type RefObject } from 'react';

/**
 * The tooltip for text that may be cut off with an ellipsis: `measure` (on hover or focus) checks whether the
 * text actually overflows its box (one line or a line clamp) and only then sets `title` to the whole text.
 *
 * Put `title` and `measure` on the element the pointer is really over. Text inside a button element must hand them
 * to the button: Firefox and Safari show only the button's own tooltip, never one on an element inside it.
 */
export function useTruncatedTitle<T extends HTMLElement>(text: string | undefined): {
  ref: RefObject<T | null>;
  title: string | undefined;
  measure: () => void;
} {
  const ref = useRef<T | null>(null);
  const [title, setTitle] = useState<string | undefined>(undefined);
  const measure = useCallback(() => {
    const node = ref.current;
    if (!node || !text) return;
    const cut = node.scrollWidth > node.clientWidth + 1 || node.scrollHeight > node.clientHeight + 1;
    setTitle(cut ? text : undefined);
  }, [text]);
  return { ref, title, measure };
}

/** Cut-off text that carries its own tooltip — for text that is NOT inside a button (see above). The
 *  truncation itself is the caller's CSS (`className`). */
export function TruncatedText({
  text,
  className,
  component = 'span',
  children,
}: {
  /** The whole text: what the tooltip says, and what renders when no `children` are passed. */
  text: string;
  className?: string;
  component?: ElementType;
  children?: ReactNode;
}) {
  const { ref, title, measure } = useTruncatedTitle<HTMLElement>(text);
  const Component = component;
  return (
    <Component ref={ref} className={className} title={title} onMouseEnter={measure} onFocus={measure}>
      {children ?? text}
    </Component>
  );
}
